import { Location } from '@cobliteam/common-types';
import LoadingPage from '@cobliteam/dashboard/src/pages/LoadingPage';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { lazy } from '@cobliteam/lazy-with-retries';
import Page from '@cobliteam/ui-business/components/layout/Page';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import AIChatSidebar from '../components/AIChatSidebar';

const AIPage = lazy(() => import('../pages/AIPage'));
const AIAutomations = lazy(() => import('../pages/AIAutomations'));
const AIAutomationRuns = lazy(() => import('../pages/AIAutomationRuns'));
const AIFiles = lazy(() => import('../pages/AIFiles'));

const AIRouter = () => {
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const { hide } = useChatWidget();

  useEffect(() => {
    hide(true);
    return () => {
      hide(false);
    };
  }, [hide]);

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing && containerRef.current) {
        const containerLeft = containerRef.current.getBoundingClientRect().left;
        const newWidth = Math.max(240, Math.min(mouseMoveEvent.clientX - containerLeft, 800));
        setSidebarWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }

    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <>
      <Page showFooter={false} className='p-0' container={false}>
        <section className='flex h-screen-without-header' ref={containerRef}>
          <AIChatSidebar width={sidebarWidth} status='idle' stop={() => {}} clearError={() => {}} setMessages={() => {}} />
          <div className='w-1 cursor-col-resize transition-colors hover:bg-primary active:bg-primary' onMouseDown={startResizing} />
          <div className='relative mx-auto flex h-screen-without-header flex-1 flex-col justify-between overflow-hidden'>
            <Switch>
              <Route exact path={Location.AI}>
                <Suspense fallback={<LoadingPage message='Carregando chat...' />}>
                  <AIPage />
                </Suspense>
              </Route>
              <Route exact path={Location.AI_AUTOMATION}>
                <Suspense fallback={<LoadingPage message='Carregando automações...' />}>
                  <AIAutomations />
                </Suspense>
              </Route>
              <Route exact path={Location.AI_AUTOMATION_RUNS}>
                <Suspense fallback={<LoadingPage message='Carregando execuções...' />}>
                  <AIAutomationRuns />
                </Suspense>
              </Route>
              <Route exact path={Location.AI_FILES}>
                <Suspense fallback={<LoadingPage message='Carregando arquivos...' />}>
                  <AIFiles />
                </Suspense>
              </Route>
            </Switch>
          </div>
        </section>
      </Page>
      <div className='absolute right-0 bottom-0 z-50 h-[6rem] w-[6rem] overflow-hidden'>
        <div className='-right-4 -translate-x-[-1rem] -rotate-45 absolute bottom-4 flex h-7 w-[120px] origin-center translate-y-[0.1rem] items-center justify-center rounded-md bg-[#FFA931] px-4 font-semibold text-black text-md'>
          BETA
        </div>
      </div>
    </>
  );
};

export default AIRouter;
